import orderBy from 'lodash/orderBy';
import { VasttrafikGidHelper } from '../../../../../helpers/vasttrafik-gid.helper';
import useFetch, { FetchConfig, Status } from '../../../../../hooks/fetch.hook';
import { IStopArea } from '../../../../../models/stop-area.model';
import { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import { t } from '../../../../../services/texts.service';
import TrafficSituationApiModel from '../../../../TrafficInformation/models/traffic-situation.api-model';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import LineListFact from '../../../../TrafficInformation/components/facts/LineListFact';
import Fact from '../../../../Shared/Fact';
import { SeverityAndTitleFactHelper } from '../../../../TrafficInformation/components/facts/SeverityAndTitleFact/severity-and-title-fact.helper';
import Spinner from '../../../../Shared/Spinner';
import Alert from '../../../../Shared/Alert';
import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';
import { SettingsHelper } from '../../helpers/settings.helper';
import { Icon } from '../../../../Shared/Icon';
import { IconMotif } from '../../../../Shared/Icon/icon-motif.enum';
import { IconColor } from '../../../../Shared/Icon/icon-color.enum';
import type { TransportMode } from '../../../../../models/transport-mode.enum';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { TrafficSituationsFilterHelper } from '../../helpers/traffic-situations-filter.helper';

function processReceivedData(data: TrafficSituationApiModel[]): ITrafficSituation[] {
  return data?.map((ts) => TrafficSituationApiModel.mapToBusinessModel(ts)) ?? [];
}

export default function TrafficSituations({
  stopAreas,
  autoRefreshTimestamp,
  journeyLineGids,
  journeyTransportModes,
  className,
}: {
  stopAreas: IStopArea[];
  autoRefreshTimestamp: DateTime;
  journeyLineGids?: string[];
  journeyTransportModes?: TransportMode[];
  className?: string;
}) {
  // === STATE ===
  const [{ status, data }] = useFetch<TrafficSituationApiModel[]>('/api/trafficsituations/all', false, false, FetchConfig.default, [autoRefreshTimestamp.toMillis()]);
  const [hiddenSituationNumbers, setHiddenSituationNumbers] = useState<string[]>(SettingsHelper.getHiddenTrafficSituationNumbers());

  // === VARIABLES ===
  const stopAreaGids = new Set(stopAreas.map((stopArea) => stopArea.gid));
  const receivedTrafficSituations = processReceivedData(data ?? []);
  const journeyLineGidSet = journeyLineGids && journeyLineGids.length > 0 ? new Set(journeyLineGids) : undefined;
  const notHiddenTrafficSituations = receivedTrafficSituations.filter((ts) => !hiddenSituationNumbers.includes(ts.situationNumber));
  const trafficSituationsForStopArea = notHiddenTrafficSituations.filter((ts) =>
    ts.affectedStopPoints.some((sp) => stopAreaGids.has(VasttrafikGidHelper.getStopAreaGidFromStopPointGid(sp.gid) ?? '')),
  );
  const filteredTrafficSituations = trafficSituationsForStopArea.filter((ts) => {
    const lines = TrafficSituationHelper.getDistinctLines(ts);

    if (TrafficSituationsFilterHelper.trafficSituationLinesMatchJourneyLine(lines, journeyLineGidSet)) return true;

    return TrafficSituationsFilterHelper.trafficSituationMatchesJourneyTransportModeFallback(lines, journeyTransportModes);
  });
  const filteredAndOrderedTrafficSituations = orderBy(filteredTrafficSituations, [(ts) => TrafficSituationHelper.toSeveritySortOrder(ts.severity), (ts) => ts.startTime], 'desc');
  const noTrafficSituationsText =
    stopAreas.length === 1
      ? t('Inga aktuella störningar för din hållplats.', 'No current disruptions for your stop area.')
      : t('Inga aktuella störningar för dina hållplatser.', 'No current disruptions for your stop areas.');

  // === EFFECTS ===
  useEffect(() => {
    if (status === Status.success) {
      const received = receivedTrafficSituations.map((ts) => ts.situationNumber);
      const hiddenInSettings = SettingsHelper.getHiddenTrafficSituationNumbers();
      const stillValidHidden = hiddenInSettings.filter((n) => received.includes(n));

      if (stillValidHidden.length !== hiddenInSettings.length) {
        SettingsHelper.saveHiddenTrafficSituationNumbers(stillValidHidden);
      }
    }
  }, [status, receivedTrafficSituations]);

  // === EVENT HANDLERS ===
  function handleHideTrafficSituation(situationNumber: string, title: string) {
    if (!hiddenSituationNumbers.includes(situationNumber)) {
      AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Dölj störning', title);
      SettingsHelper.saveHiddenTrafficSituationNumbers([...hiddenSituationNumbers, situationNumber]);
      setHiddenSituationNumbers((prev) => [...prev, situationNumber]);
    }
  }

  function handleResetHiddenSituations() {
    SettingsHelper.saveHiddenTrafficSituationNumbers([]);
    setHiddenSituationNumbers([]);
  }

  return (
    <div className={className ?? ''}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0">{t('Trafikinformation', 'Traffic information')}</h3>
        {hiddenSituationNumbers.length > 0 && (
          <button
            className="btn btn-tertiary btn-tertiary-position-right d-flex align-items-center"
            aria-label={t('Återställ dolda störningar', 'Reset hidden disruptions')}
            onClick={handleResetHiddenSituations}
          >
            <Icon motif={IconMotif.RotateLeft} color={IconColor.Blue1000} className="me-1" altText="" />
            {t('Återställ', 'Reset')}
          </button>
        )}
      </div>
      {status === Status.loading && <Spinner className="my-3" />}
      <Alert
        heading={t('Ett fel uppstod vid hämtning av trafikinformation. Försök igen senare.', 'An error occurred while fetching traffic information. Please try again later.')}
        closable={false}
        show={status === Status.failed}
      />
      {status === Status.success && (
        <>
          {filteredAndOrderedTrafficSituations.length === 0 && <div>{noTrafficSituationsText}</div>}
          {filteredAndOrderedTrafficSituations.length > 0 && (
            <ul className="list-unstyled d-flex flex-column gap-2">
              {filteredAndOrderedTrafficSituations.map((ts) => (
                <li key={ts.situationNumber}>
                  <TrafficSituation trafficSituation={ts} onHide={handleHideTrafficSituation} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function TrafficSituation({ trafficSituation, onHide }: { trafficSituation: ITrafficSituation; onHide?: (situationNumber: string, title: string) => void }) {
  return (
    <div className="d-flex flex-column gap-2 p-2 p-sm-3 bg-white border rounded position-relative">
      <button type="button" className="btn-close position-absolute top-0 end-0" aria-label={t('Dölj', 'Hide')} onClick={() => onHide?.(trafficSituation.situationNumber, trafficSituation.title)} />
      <Fact {...SeverityAndTitleFactHelper.getSeverityIcon(trafficSituation.severity)} className="pe-3" altText={SeverityAndTitleFactHelper.getSeverityIconAltText(trafficSituation.severity)}>
        <h4 className="text-break mb-0">{trafficSituation.title}</h4>
      </Fact>
      <LineListFact trafficSituation={trafficSituation} useShortName={true} />
      <div className="d-flex flex-row justify-content-between">
        <a href={`https://www.vasttrafik.se/reseplanering/trafikinformation#${encodeURIComponent(trafficSituation.situationNumber)}`} target="_blank" rel="noopener noreferrer">
          <span>{t('Läs mer', 'Read more')}</span>
          <span className="visually-hidden">
            {t('om', 'about')} {trafficSituation.title}
          </span>
        </a>
      </div>
    </div>
  );
}